import type { z } from "zod";

/**
 * Contract primitives (types + `defineBedrockEndpointContract`) for internal Bedrock API endpoints. Depends on nothing in the `bedrockApi` tree, so leaves and the registry can import it without a cycle; lives in `packages/schemas` (no hono) so clients like `bedrock-sdk` share the same schema objects. Auth, rate limiting, and handlers are server behavior declared at the route site, not part of the contract.
 */

type BedrockEndpointMethod = "get" | "post" | "put" | "patch" | "delete";

/** Endpoint whose success body is a raw byte stream (e.g. asset content); no zod schema because there is no JSON to validate. */
export type BedrockBinaryEndpointResponse = { type: "binary" };

/** JSON success body, carried behind the same `type` discriminant as every response kind so consumers dispatch on one closed union. */
export type BedrockJsonEndpointResponse<
  ResponseSchemaType extends z.ZodTypeAny = z.ZodTypeAny,
> = { type: "json"; schema: ResponseSchemaType };

export type BedrockEndpointRequestSchemas = {
  body?: z.ZodTypeAny;
  // query/path must be object schemas: OpenAPI serializes each key as one parameter.
  query?: z.ZodObject;
  params?: z.ZodObject;
};

export type BedrockEndpointStability = "default" | "beta";

type BedrockEndpointContractBase<
  RequestSchemasType extends
    BedrockEndpointRequestSchemas = BedrockEndpointRequestSchemas,
> = {
  /** Globally unique `group.method` identifier, used as the OpenAPI operationId. */
  operationId: string;
  method: BedrockEndpointMethod;
  /** Full client-facing request path (mount point + `routePath`) in OpenAPI `{param}` syntax. */
  path: string;
  /** Path under the group's mount point used for server route registration; invariant: mount point + `routePath` === `path`. */
  routePath: string;
  /** Human-readable summary, used as the success response description in docs. */
  description: string;
  /** Contract maturity; `"beta"` renders a Beta banner and `x-beta` marker in public OpenAPI docs. */
  stability?: BedrockEndpointStability;
  /** Success status code for the endpoint. Defaults to 200 when omitted. */
  successStatus?: 200 | 201 | 202;
  request?: RequestSchemasType;
};

/** Contract whose success response is a JSON document; response-projecting surfaces (e.g. the public API versioning layer) constrain to this member. */
export type BedrockJsonEndpointContract<
  RequestSchemasType extends
    BedrockEndpointRequestSchemas = BedrockEndpointRequestSchemas,
  ResponseSchemaType extends z.ZodTypeAny = z.ZodTypeAny,
> = BedrockEndpointContractBase<RequestSchemasType> & {
  response: BedrockJsonEndpointResponse<ResponseSchemaType>;
};

/** A contract whose success response is a raw byte stream. */
export type BedrockBinaryEndpointContract<
  RequestSchemasType extends
    BedrockEndpointRequestSchemas = BedrockEndpointRequestSchemas,
> = BedrockEndpointContractBase<RequestSchemasType> & {
  response: BedrockBinaryEndpointResponse;
};

/** Endpoint whose success response is an HTTP redirect (browser bounce flows, e.g. Stripe onboarding); the handler returns the redirect Response itself, so there is no body schema. */
export type BedrockRedirectEndpointResponse = { type: "redirect" };

/** A contract whose success response is an HTTP redirect. `successStatus` is banned — redirects are always documented as 302. */
export type BedrockRedirectEndpointContract<
  RequestSchemasType extends
    BedrockEndpointRequestSchemas = BedrockEndpointRequestSchemas,
> = BedrockEndpointContractBase<RequestSchemasType> & {
  response: BedrockRedirectEndpointResponse;
  successStatus?: never;
};

export type BedrockEndpointContract =
  | BedrockJsonEndpointContract
  | BedrockBinaryEndpointContract
  | BedrockRedirectEndpointContract;

type InferOrUndefined<SchemaType> = SchemaType extends z.ZodTypeAny
  ? z.infer<SchemaType>
  : undefined;

type RequestSchemaAt<
  ContractType extends BedrockEndpointContract,
  RequestKey extends keyof BedrockEndpointRequestSchemas,
> = ContractType["request"] extends undefined
  ? undefined
  : ContractType["request"] extends {
        readonly [Key in RequestKey]?: infer SchemaType;
      }
    ? SchemaType
    : undefined;

/** Validated request input: each of body/query/path is the parsed schema output, or `undefined` when the contract declares no schema for that part. */
export type BedrockEndpointInput<ContractType extends BedrockEndpointContract> =
  {
    body: InferOrUndefined<RequestSchemaAt<ContractType, "body">>;
    query: InferOrUndefined<RequestSchemaAt<ContractType, "query">>;
    path: InferOrUndefined<RequestSchemaAt<ContractType, "params">>;
  };

/** Inferred JSON response type; binary (and any non-JSON) contracts extract to `never` so JSON-only consumers fail loudly. */
export type BedrockEndpointResponse<
  ContractType extends BedrockEndpointContract,
> = z.infer<
  Extract<ContractType, BedrockJsonEndpointContract>["response"]["schema"]
>;

/** Identity function that preserves the literal contract type, so consumers infer exact request/response types per registry entry. */
export function defineBedrockEndpointContract<
  const ContractType extends BedrockEndpointContract,
>(contract: ContractType): ContractType {
  return contract;
}
